import React from "react";
import type { Component } from "../../content/schema-types";
import { useContent } from "../../hooks";
import { useLocalizedUrl } from "../../hooks/useLocalizedUrl";
import { Button, LanguageSwitcher } from "../ui";
import { HeaderLogo } from "./HeaderLogo";
import { MainNavigation } from "./MainNavigation";
import { MobileNavigation } from "./MobileNavigation";

export type HeaderProps = Extract<Component, { type: "header" }>["content"];

export const Header: React.FC<HeaderProps> = ({
    logo,
    mainNav,
    language = "Deutsch",
    cta,
    languageSwitcherVariant = "light",
}) => {
    const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
    const { common } = useContent();
    const finalCtaLabel = cta?.label || common?.cta?.signIn;
    const ctaHrefRaw = cta?.href || "/anmelden";
    const ctaHref = useLocalizedUrl(ctaHrefRaw);

    return (
        <header className="absolute top-0 left-0 right-0 z-50 py-6">
            <div className="mx-auto w-full px-4 md:px-12">
                <div className="rounded-full bg-nav-background shadow-lg pl-8 pr-4 py-4 flex items-center justify-between backdrop-blur-[12px]">
                    <HeaderLogo logo={logo} />

                    <MainNavigation items={mainNav} />

                    {/*
                        `whitespace-nowrap` wie an der Navigation: das globale
                        `word-break: break-word` aus `src/index.css` brach auch
                        den Sprachumschalter («Deutsc/h») und den CTA
                        («Kontak/t») mitten im Wort. Begruendung siehe
                        `MainNavigation.tsx`.
                    */}
                    <div className="hidden xl:flex items-center gap-4 whitespace-nowrap shrink-0">
                        <LanguageSwitcher
                            variant={
                                languageSwitcherVariant === "dark"
                                    ? "dark"
                                    : languageSwitcherVariant === "transparent-dark"
                                      ? "transparent-dark"
                                      : "light"
                            }
                        />
                        <Button href={ctaHref} className="!bg-nav-cta">
                            {finalCtaLabel}
                        </Button>
                    </div>

                    <button
                        type="button"
                        className="xl:hidden p-2 text-nav-cta min-w-[44px] min-h-[44px] flex items-center justify-center"
                        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                        aria-expanded={mobileMenuOpen}
                        aria-label={mobileMenuOpen ? common?.navigation?.closeMenu : common?.navigation?.openMenu}
                        data-testid="mobile-menu-button"
                    >
                        <div className="relative w-6 h-6">
                            <span
                                className={`absolute left-0 block w-6 h-0.5 bg-current transform transition-all duration-300 ${
                                    mobileMenuOpen ? "top-[11px] rotate-45" : "top-[3px]"
                                }`}
                            />
                            <span
                                className={`absolute left-0 top-[11.5px] block w-6 h-0.5 bg-current transition-opacity duration-300 ${
                                    mobileMenuOpen ? "opacity-0" : "opacity-100"
                                }`}
                            />
                            <span
                                className={`absolute left-0 block w-6 h-0.5 bg-current transform transition-all duration-300 ${
                                    mobileMenuOpen ? "top-[11px] -rotate-45" : "top-[20px]"
                                }`}
                            />
                        </div>
                    </button>
                </div>
            </div>

            <MobileNavigation
                isOpen={mobileMenuOpen}
                onClose={() => setMobileMenuOpen(false)}
                items={mainNav || []}
                language={language}
                ctaLabel={finalCtaLabel}
                ctaHref={ctaHref}
            />
        </header>
    );
};

export default Header;
